import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/features/auth/store";
import { api, ApiError, tokens } from "@/lib/api-client";

/**
 * Accepting an invitation. Deliberately outside the `_app` layout: the person has no session yet,
 * so the authenticated shell would bounce them straight to /login.
 */
export const Route = createFileRoute("/invite/$token")({ component: Accept });

function Accept() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const bootstrap = useAuth((s) => s.bootstrap);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tooShort = password.length > 0 && password.length < 10;
  const mismatch = confirm.length > 0 && confirm !== password;
  const ready = password.length >= 10 && confirm === password && !busy;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api<{ accessToken: string; refreshExpiresAt: string }>(
        "/auth/invite/accept",
        { method: "POST", json: { token, password }, anonymous: true }
      );
      tokens.set(r.accessToken);
      await bootstrap();
      toast.success("Welcome. Your account is active.");
      void navigate({ to: "/" });
    } catch (e) {
      // An access window that has not opened yet is not a failed activation: the password is
      // saved, only the session is withheld. Say so, or they will keep retrying the invite.
      if (e instanceof ApiError && e.code === "access_window_inactive")
        setError(`Your password is set. ${e.message} Sign in from then on.`);
      // A handheld-only account: the password works, just not here.
      else if (e instanceof ApiError && e.code === "no_access_on_device")
        setError(
          "Your password is set. Your account is for the handheld app — sign in there."
        );
      else
        setError(
          e instanceof ApiError
            ? e.message
            : "Could not activate the account. Ask for a fresh invitation."
        );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex min-h-dvh max-w-md items-center px-4">
      <Card className="w-full">
        <CardHeader>
          <CardTitle>Set your password</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          <p className="text-sm text-ink-muted">
            Choose a password and your NexSeed account is ready. Nobody else has
            seen or set one for you.
          </p>
          <Field
            label="New password"
            error={tooShort ? "At least 10 characters." : undefined}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
          <Field
            label="Confirm password"
            error={mismatch ? "The two do not match." : undefined}
          >
            <Input
              type="password"
              autoComplete="new-password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && ready) void submit();
              }}
            />
          </Field>
          {error && <p className="text-sm text-danger-text">{error}</p>}
          <Button
            className="w-full"
            disabled={!ready}
            onClick={() => void submit()}
          >
            {busy ? "Activating…" : "Activate my account"}
          </Button>
        </CardBody>
      </Card>
    </div>
  );
}
